import { type StoredPicturesDownloadCondition } from "@/types/storedPicturesDownloadCondition";
import { type AiStoredPictureResult } from "./types";

export interface AiDownloadPreparation {
  condition: StoredPicturesDownloadCondition;
  billableCount: number;
  freeCount: number;
  estimatedChargeAmount: number;
}

export type AiDownloadPreparationResult =
  | { ok: true; value: AiDownloadPreparation }
  | { ok: false };

const isValidId = (value: number): boolean =>
  Number.isInteger(value) && value > 0;

/**
 * AI検索結果を、既存の取得済み登記ダウンロードAPIが受け取る課金・無料データへ分類する。
 * 取得に必要なIDが欠けた結果を含む場合は、誤った課金や取得を避けるため失敗を返す。
 *
 * @param results ユーザーが取得対象として選択した検索結果
 * @returns ダウンロード条件と件数・料金、または条件を作成できなかったことを示す結果
 */
export const buildAiDownloadPreparation = (
  results: AiStoredPictureResult[]
): AiDownloadPreparationResult => {
  const billableData = new Map<
    number,
    { id: number; price: number; receivedId: number }
  >();
  const noBillableData = new Map<number, { id: number }>();

  for (const result of results) {
    if (!result.isDownloadable) {
      return { ok: false };
    }

    if (!result.isChargeRequired) {
      if (!isValidId(result.pictureRequestDetailId)) {
        return { ok: false };
      }
      noBillableData.set(result.pictureRequestDetailId, {
        id: result.pictureRequestDetailId,
      });
      continue;
    }

    if (!isValidId(result.receivedId)) {
      return { ok: false };
    }
    billableData.set(result.receivedId, {
      id: result.receivedId,
      price: result.chargeAmount,
      receivedId: result.receivedId,
    });
  }

  if ([...billableData.keys()].some((id) => noBillableData.has(id))) {
    return { ok: false };
  }

  const condition = {
    billableData: [...billableData.values()],
    noBillableData: [...noBillableData.values()],
  };
  return {
    ok: true,
    value: {
      condition,
      billableCount: condition.billableData.length,
      freeCount: condition.noBillableData.length,
      estimatedChargeAmount: condition.billableData.reduce(
        (total, item) => total + item.price,
        0
      ),
    },
  };
};
